<template>
  <div class="page-vendor-detail">
  <main>
      <section class="border-b border-gray-100 bg-gray-50">
        <div class="mx-auto max-w-[1400px] px-4 py-8 lg:px-8">
          <nav class="mb-6 flex flex-wrap items-center gap-3 text-sm text-muted">
            <button class="inline-flex items-center gap-2 rounded-full border border-[#e6d5c5] bg-white px-5 py-3 font-semibold text-ink shadow-sm transition hover:border-brand hover:text-brand" @click="goBack">
              <i class="ri-arrow-left-line text-base"></i>
              <span>Back</span>
            </button>
            <span class="text-[#d2b9a3]">/</span>
            <span class="font-medium text-[#b78969]">Business</span>
            <span class="text-[#d2b9a3]">/</span>
            <RouterLink class="font-medium text-[#b78969] transition hover:text-brand" to="/vendors">{{ vendorCategory || 'Vendors' }}</RouterLink>
            <span class="text-[#d2b9a3]">/</span>
            <span class="font-semibold text-ink">{{ vendorName }}</span>
          </nav>

          <div v-if="loading" class="rounded-2xl border border-dashed border-gray-200 bg-white p-6 text-sm font-semibold text-muted">
            Loading vendor details...
          </div>
          <div v-else-if="error" class="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">
            {{ error }}
          </div>
          <div v-else class="grid gap-6 lg:grid-cols-[1.4fr_1fr] lg:items-center">
            <div class="space-y-4">
              <div class="flex flex-wrap items-center gap-3 text-xs text-muted">
                <span class="rounded-full bg-white px-3 py-1 font-semibold text-brand shadow-card ring-1 ring-gray-100">Featured</span>
                <span v-if="vendorCity" class="rounded-full bg-white px-3 py-1 font-semibold shadow-card ring-1 ring-gray-100">{{ vendorCity }}</span>
                <span v-if="vendorCategory" class="rounded-full bg-white px-3 py-1 font-semibold shadow-card ring-1 ring-gray-100">{{ vendorCategory }}</span>
              </div>
              <h1 class="text-3xl font-semibold text-ink sm:text-4xl">{{ vendorName }}</h1>
              <p class="text-lg text-muted">{{ vendorDescription }}</p>
              <div class="flex flex-wrap items-center gap-4 text-sm text-muted">
                <span class="flex items-center gap-2 rounded-full bg-white px-3 py-2 shadow-card ring-1 ring-gray-100">
                  <span class="h-2 w-2 rounded-full bg-green-500"></span> ★ {{ vendorRating }} ({{ vendorReviews }})
                </span>
                <span v-if="vendorGuests" class="rounded-full bg-white px-3 py-2 shadow-card ring-1 ring-gray-100">{{ vendorGuests }}</span>
                <span v-if="vendorPrice" class="rounded-full bg-white px-3 py-2 shadow-card ring-1 ring-gray-100">{{ vendorPrice }}</span>
              </div>
              <div class="flex flex-wrap gap-3">
                <button class="rounded-full bg-brand px-5 py-3 text-sm font-semibold text-white shadow-card transition hover:bg-brandDark">Request hold</button>
                <button class="rounded-full border border-gray-200 px-5 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand">Message vendor</button>
              </div>
            </div>
            <div class="grid gap-3 sm:grid-cols-2">
              <img class="h-52 w-full rounded-3xl object-cover sm:row-span-2 sm:h-full" :src="heroImage" :alt="vendorName">
              <img v-for="(img, idx) in heroThumbs" :key="idx" class="h-52 w-full rounded-3xl object-cover" :src="img" :alt="`${vendorName} image ${idx + 1}`">
            </div>
          </div>
        </div>
      </section>

      <section class="mx-auto max-w-[1400px] px-4 py-8 lg:px-8">
        <div class="grid gap-8 lg:grid-cols-[2fr_1fr]">
          <div class="space-y-8">
            <div class="space-y-3">
              <h2 class="text-xl font-semibold text-ink">About this vendor</h2>
              <p class="text-sm text-muted">{{ vendorDescription }}</p>
              <div class="flex flex-wrap gap-2 text-xs text-muted">
                <span v-if="vendorAddress" class="rounded-full bg-gray-50 px-3 py-2">{{ vendorAddress }}</span>
                <span v-if="vendorCity" class="rounded-full bg-gray-50 px-3 py-2">{{ vendorCity }}</span>
              </div>
            </div>

            <div class="grid gap-4 rounded-3xl bg-gray-50 p-6 ring-1 ring-gray-100 md:grid-cols-2">
              <div>
                <p class="text-sm font-semibold text-ink">Packages</p>
                <ul class="mt-2 space-y-2 text-sm text-muted">
                  <li>• Ceremony + reception (8 hours)</li>
                  <li>• Rehearsal access included</li>
                  <li>• Furniture, sound, lighting options</li>
                </ul>
              </div>
              <div>
                <p class="text-sm font-semibold text-ink">Add-ons</p>
                <ul class="mt-2 space-y-2 text-sm text-muted">
                  <li>• Day-of coordinator</li>
                  <li>• Champagne tower setup</li>
                  <li>• Afterparty lounge conversion</li>
                </ul>
              </div>
            </div>

            <div class="space-y-4">
              <h3 class="text-xl font-semibold text-ink">Gallery</h3>
              <div class="grid gap-4 md:grid-cols-3">
                <img
                  v-for="(img, idx) in galleryImages"
                  :key="img + idx"
                  class="h-40 w-full rounded-2xl object-cover"
                  :src="img"
                  :alt="`${vendorName} gallery ${idx + 1}`"
                >
              </div>
            </div>

            <div class="space-y-4">
              <h3 class="text-xl font-semibold text-ink">Reviews</h3>
              <div class="space-y-3 rounded-3xl bg-white p-4 shadow-card ring-1 ring-gray-100">
                <div class="flex items-center justify-between">
                  <div>
                    <p class="text-sm font-semibold text-ink">Diana & Louis</p>
                    <p class="text-xs text-muted">Mar 2024</p>
                  </div>
                  <div class="rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700">★ 4.9</div>
                </div>
                <p class="text-sm text-muted">The light here is unreal. The team handled weather pivots effortlessly and worked seamlessly with our planner.</p>
              </div>
              <div class="space-y-3 rounded-3xl bg-white p-4 shadow-card ring-1 ring-gray-100">
                <div class="flex items-center justify-between">
                  <div>
                    <p class="text-sm font-semibold text-ink">Amara & Jeff</p>
                    <p class="text-xs text-muted">Jan 2024</p>
                  </div>
                  <div class="rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700">★ 5.0</div>
                </div>
                <p class="text-sm text-muted">Loved the indoor-outdoor flow and how transparent the pricing was. No surprises, just great vibes.</p>
              </div>
            </div>
          </div>

          <aside class="space-y-4 rounded-3xl bg-white p-6 shadow-card ring-1 ring-gray-100">
            <div class="space-y-2">
              <p class="text-sm font-semibold text-ink">Pricing</p>
              <p class="text-3xl font-semibold text-ink">{{ vendorPrice || 'Contact for pricing' }}</p>
              <p class="text-sm text-muted">Packages and add-ons vary by vendor.</p>
            </div>
            <div class="space-y-2">
              <p class="text-sm font-semibold text-ink">Contact</p>
              <p class="text-sm text-muted" v-if="vendorPhone">Phone: {{ vendorPhone }}</p>
              <p class="text-sm text-muted" v-if="vendorEmail">Email: {{ vendorEmail }}</p>
              <p class="text-sm text-muted" v-if="vendorWebsite"><a class="font-semibold text-brand hover:text-brandDark" :href="vendorWebsite" target="_blank" rel="noopener">Website →</a></p>
            </div>
            <button class="w-full rounded-2xl bg-brand px-4 py-3 text-sm font-semibold text-white transition hover:bg-brandDark">Request hold</button>
            <button class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand">Message vendor</button>
            <div class="rounded-2xl bg-gray-50 p-4" v-if="vendorCity">
              <p class="text-sm font-semibold text-ink">Location</p>
              <p class="text-sm text-muted">{{ vendorAddress || vendorCity }}</p>
            </div>
          </aside>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import api from '@/services/user/api'

const FALLBACK_IMAGE = 'https://dummyimage.com/800x600/efe7ff/251833'

export default {
  name: 'VendorDetailView',
  data() {
    return {
      vendor: null,
      loading: true,
      error: ''
    }
  },
  computed: {
    vendorSlug() {
      const param =
        this.$route.params?.username ||
        this.$route.params?.slug ||
        this.$route.params?.id ||
        ''
      if (param) return param
      const match = (this.$route.path || '').match(/\/vendor\/([^/?#]+)/i)
      return match ? decodeURIComponent(match[1]) : ''
    },
    vendorName() {
      return this.vendor?.s_business || this.vendor?.s_name || 'Vendor Name'
    },
    vendorDescription() {
      return this.vendor?.s_desc || this.vendor?.detail_links?.find?.((l) => l.title === 'Description')?.value || 'No description available.'
    },
    vendorCity() {
      return this.vendor?.city || this.vendor?.s_city || ''
    },
    vendorCategory() {
      return this.vendor?.businesscategory || this.vendor?.s_category || ''
    },
    vendorRating() {
      return this.vendor?.business_star_count || this.vendor?.rating || '4.9'
    },
    vendorReviews() {
      return this.vendor?.review_business_count || this.vendor?.reviews || '0'
    },
    vendorGuests() {
      return this.vendor?.guests_label || ''
    },
    vendorPrice() {
      return this.vendor?.price_range || ''
    },
    vendorAddress() {
      return this.vendor?.s_address || this.vendor?.s_address1 || ''
    },
    vendorPhone() {
      return this.vendor?.s_phone ? `${this.vendor?.s_phone_code || ''} ${this.vendor?.s_phone}`.trim() : ''
    },
    vendorEmail() {
      return this.vendor?.s_email || ''
    },
    vendorWebsite() {
      return this.vendor?.website_link || ''
    },
    heroImage() {
      return this.vendor?.banner_image || this.galleryImages[0] || FALLBACK_IMAGE
    },
    heroThumbs() {
      return this.galleryImages.slice(1, 3)
    },
    galleryImages() {
      const imgs = (this.vendor?.s_images || []).map((i) => i.simg_img).filter(Boolean)
      if (imgs.length) return imgs
      return [FALLBACK_IMAGE, 'https://dummyimage.com/800x600/f5f5f5/111827', 'https://dummyimage.com/800x600/fff3ea/111827']
    }
  },
  methods: {
    goBack() {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.push('/vendors')
      }
    },
    async fetchVendor() {
      this.loading = true
      this.error = ''
      try {
        const formData = new FormData()
        formData.append('businessid', this.vendorSlug)
        formData.append('userid', '1')
        const { data } = await api.getVendorDetail(formData)
        const detail = data?.data?.[0] || null
        if (!detail) {
          this.error = 'Vendor not found.'
        }
        this.vendor = detail
      } catch (err) {
        console.error(err)
        this.error = 'Failed to load vendor details.'
      } finally {
        this.loading = false
      }
    }
  },
  watch: {
    '$route.params.username': 'fetchVendor',
    '$route.params.slug': 'fetchVendor',
    '$route.path': 'fetchVendor'
  },
  mounted() {
    this.fetchVendor()
  }
}
</script>
